// Data Access Control service — talks to manager-service.
//
// Endpoints (mounted at /api/manager/api/data-permissions):
//   GET  /users/:userId      — admin: load denials for a user
//   PUT  /users/:userId      — admin: replace the denial set
//   GET  /me                 — current user's own denials (used by UI
//                              hooks to grey-out denied schemas / views)

const BASE = '/api/manager/api/data-permissions';

function authHeaders(extra = {}) {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
}

async function _json(response) {
  if (!response.ok) {
    let detail = `HTTP ${response.status}`;
    try {
      const body = await response.json();
      detail = body.detail || body.message || detail;
    } catch { /* no body */ }
    const err = new Error(detail);
    err.status = response.status;
    throw err;
  }
  return response.json();
}

/**
 * Load denials for a specific user.
 * @param {number|string} userId
 * @returns {Promise<{user_id, is_admin, denials: Array<{schema_name, table_name}>}>}
 */
export async function getUserPermissions(userId) {
  const response = await fetch(`${BASE}/users/${userId}`, {
    method: 'GET',
    headers: authHeaders(),
  });
  return _json(response);
}

/**
 * Replace the user's denial set.
 *
 * Accepts either the legacy positional form (denials array) or a config
 * object describing the new group-aware payload:
 *   { mode: 'group' | 'group_with_overrides' | 'custom',
 *     groupId: number | null,
 *     denials: Array<{schema_name, table_name?}> }
 *
 * For backward compatibility, passing an array as the second argument
 * is treated as ``mode='custom'`` with that denial set.
 */
export async function setUserPermissions(userId, payload) {
  let body;
  if (Array.isArray(payload)) {
    // Legacy: array of denials → custom mode.
    body = { mode: 'custom', group_id: null, denials: payload };
  } else {
    const { mode, groupId, denials = [] } = payload || {};
    body = {
      mode: mode || 'custom',
      group_id: groupId ?? null,
      denials,
    };
  }
  const response = await fetch(`${BASE}/users/${userId}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
  return _json(response);
}

/**
 * Return the current user's denial set (admin always returns []).
 * Cached by the manager service in memory; safe to call on every page mount.
 */
export async function getMyPermissions() {
  const response = await fetch(`${BASE}/me`, {
    method: 'GET',
    headers: authHeaders(),
  });
  return _json(response);
}
